/**
 * New vs Returning Customer Revenue Chart Component
 *
 * Donut chart comparing revenue from new vs returning customers.
 * Shows percentages and customer counts.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import type { NewVsReturningRevenueData } from '../../../types/financialInsights';
import {
  formatChartCurrency,
  calculatePercentage,
} from '../../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface NewVsReturningChartProps {
  data: NewVsReturningRevenueData | undefined;
  loading: boolean;
}

const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: 350 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="rectangular" width={250} height={32} />
    </Box>
    <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto' }} />
  </Box>
);

export const NewVsReturningChart: React.FC<NewVsReturningChartProps> = ({
  data,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <ChartSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{ height: 350 }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No customer revenue data found for this period
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const totalRevenue =
    (data.new_customer_revenue || 0) + (data.returning_customer_revenue || 0);

  const chartData = {
    labels: ['New Customers', 'Returning Customers'],
    datasets: [
      {
        data: [data.new_customer_revenue, data.returning_customer_revenue],
        backgroundColor: ['#4caf50', theme.palette.primary.main],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          color: theme.palette.text.primary,
          font: { family: 'Arial, sans-serif', size: 11 },
          generateLabels: (chart: any) => {
            const { datasets } = chart.data;
            return chart.data.labels.map((label: string, i: number) => {
              const value = datasets[0].data[i];
              const percentage = calculatePercentage(value, totalRevenue);
              const count =
                i === 0
                  ? data.new_customer_count
                  : data.returning_customer_count;
              return {
                text: `${label} (${percentage}) - ${count} customers`,
                fillStyle: datasets[0].backgroundColor[i],
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        callbacks: {
          label: (context: any) => {
            const idx = context.dataIndex;
            const revenue =
              idx === 0
                ? data.new_customer_revenue
                : data.returning_customer_revenue;
            const count =
              idx === 0
                ? data.new_customer_count
                : data.returning_customer_count;
            const percentage = calculatePercentage(revenue, totalRevenue);
            return [
              `Revenue: ${formatChartCurrency(revenue)}`,
              `Customers: ${count}`,
              `Percentage: ${percentage}`,
            ];
          },
        },
      },
    },
    cutout: '65%',
  };

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            New vs Returning Customer Revenue
          </Typography>
        </Box>

        <Box sx={{ height: 250, position: 'relative' }}>
          <Doughnut data={chartData} options={chartOptions} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '25%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Total Revenue
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatChartCurrency(totalRevenue, 'USD', true)}
            </Typography>
          </Box>
        </Box>

        <Box mt={2} display="flex" justifyContent="center" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {data.new_customer_count + data.returning_customer_count} total
            customers
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewVsReturningChart;
